import { lazy, Suspense, type ReactNode } from 'react'
import { Navigate, Route, Routes } from 'react-router-dom'
import { useAuth } from '../features/auth/useAuth'
import { AccessDeniedScreen, AccountPendingScreen, AuthErrorScreen, LoadingScreen } from '../features/auth/AuthScreens'
import { LoginPage } from '../features/auth/LoginPage'
import { AdminFeatureGate, ProtectedRoute, RoleGate } from '../features/auth/RouteGuards'
import type { AdminFeature } from '../config/featureFlags'
import { AdminShell } from '../features/admin/AdminShell'
import { getAuthorizationDecision } from '../features/auth/authResolution'

const AdminDashboard = lazy(() => import('../features/admin/AdminDashboard').then((module) => ({ default: module.AdminDashboard })))
const AdminStudentsPage = lazy(() => import('../features/students/AdminStudentsPage').then((module) => ({ default: module.AdminStudentsPage })))
const AdminTeachersPage = lazy(() => import('../features/teachers/AdminTeachersPage').then((module) => ({ default: module.AdminTeachersPage })))
const AdminRecordsPage = lazy(() => import('../features/records/AdminRecordsPage').then((module) => ({ default: module.AdminRecordsPage })))
const AdminAccountsPage = lazy(() => import('../features/accounts/AdminAccountsPage').then((module) => ({ default: module.AdminAccountsPage })))
const AdminAssignmentsPage = lazy(() => import('../features/assignments/AdminAssignmentsPage').then((module) => ({ default: module.AdminAssignmentsPage })))
const AdminCallAttemptsPage = lazy(() => import('../features/employee/AdminCallAttemptsPage').then((module) => ({ default: module.AdminCallAttemptsPage })))
const AdminInactiveTrashPage = lazy(() => import('../features/trash/AdminInactiveTrashPage').then((module) => ({ default: module.AdminInactiveTrashPage })))
const AdminRemindersPage = lazy(() => import('../features/reminders/AdminRemindersPage').then((module) => ({ default: module.AdminRemindersPage })))
const AdminFeesPage = lazy(() => import('../features/fees/AdminFeesPage').then((module) => ({ default: module.AdminFeesPage })))
const AdminPaymentsPage = lazy(() => import('../features/payments/AdminPaymentsPage').then((module) => ({ default: module.AdminPaymentsPage })))
const AdminNotificationStatusPage = lazy(() => import('../features/notifications/AdminNotificationStatusPage').then((module) => ({ default: module.AdminNotificationStatusPage })))
const AdminReportsPage = lazy(() => import('../features/reports/AdminReportsPage').then((module) => ({ default: module.AdminReportsPage })))
const EmployeeDueCallingPage = lazy(() => import('../features/employee/EmployeeDueCallingPage').then((module) => ({ default: module.EmployeeDueCallingPage })))
const ParentDashboard = lazy(() => import('../features/parent/ParentDashboard').then((module) => ({ default: module.ParentDashboard })))

function LandingRoute() {
  const auth = useAuth()
  const decision = getAuthorizationDecision(auth)

  if (decision === 'loading') return <LoadingScreen label="Loading your account..." />
  if (decision === 'signed_out') return <Navigate to="/login" replace />
  if (decision === 'error') return <AuthErrorScreen message={auth.error ?? 'Account access could not be checked.'} />
  if (decision === 'pending') return <AccountPendingScreen />

  return <Navigate to={`/${auth.role}`} replace />
}

function RouteFallback() {
  return <LoadingScreen label="Loading page..." />
}

function RolePage({ role, feature, children }: { role: 'admin' | 'employee' | 'parent'; feature?: AdminFeature; children: ReactNode }) {
  const content = role === 'admin' && feature
    ? <AdminFeatureGate feature={feature}>{children}</AdminFeatureGate>
    : children

  return (
    <ProtectedRoute>
      <RoleGate role={role}>
        <Suspense fallback={<RouteFallback />}>
          {role === 'admin' ? <AdminShell>{content}</AdminShell> : content}
        </Suspense>
      </RoleGate>
    </ProtectedRoute>
  )
}

export function AppRoutes() {
  return (
    <Routes>
      <Route path="/login" element={<LoginPage />} />
      <Route path="/" element={<LandingRoute />} />
      <Route path="/admin" element={<RolePage role="admin" feature="dashboard"><AdminDashboard /></RolePage>} />
      <Route path="/admin/students" element={<RolePage role="admin" feature="students"><AdminStudentsPage /></RolePage>} />
      <Route path="/admin/teachers" element={<RolePage role="admin" feature="teachers"><AdminTeachersPage /></RolePage>} />
      <Route path="/admin/onboarding" element={<Navigate to="/admin/students?add=1" replace />} />
      <Route path="/admin/records" element={<RolePage role="admin" feature="records"><AdminRecordsPage /></RolePage>} />
      <Route path="/admin/accounts" element={<RolePage role="admin" feature="accounts"><AdminAccountsPage /></RolePage>} />
      <Route path="/admin/assignments" element={<RolePage role="admin" feature="assignments"><AdminAssignmentsPage /></RolePage>} />
      <Route path="/admin/call-attempts" element={<RolePage role="admin" feature="callFollowups"><AdminCallAttemptsPage /></RolePage>} />
      <Route path="/admin/trash" element={<RolePage role="admin" feature="trash"><AdminInactiveTrashPage /></RolePage>} />
      <Route path="/admin/reminders" element={<RolePage role="admin" feature="reminders"><AdminRemindersPage /></RolePage>} />
      <Route path="/admin/fees" element={<RolePage role="admin" feature="fees"><AdminFeesPage /></RolePage>} />
      <Route path="/admin/payments" element={<RolePage role="admin" feature="payments"><AdminPaymentsPage /></RolePage>} />
      <Route path="/admin/notifications" element={<RolePage role="admin" feature="notificationsDiagnostics"><AdminNotificationStatusPage /></RolePage>} />
      <Route path="/admin/reports" element={<RolePage role="admin" feature="reports"><AdminReportsPage /></RolePage>} />
      <Route path="/employee" element={<RolePage role="employee"><EmployeeDueCallingPage /></RolePage>} />
      <Route path="/employee/due-calls" element={<RolePage role="employee"><EmployeeDueCallingPage /></RolePage>} />
      <Route path="/parent" element={<RolePage role="parent"><ParentDashboard /></RolePage>} />
      <Route path="/parent/dashboard" element={<RolePage role="parent"><ParentDashboard /></RolePage>} />
      <Route path="/pending" element={<ProtectedRoute><AccountPendingScreen /></ProtectedRoute>} />
      <Route path="/denied" element={<AccessDeniedScreen />} />
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  )
}
